import {Component, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy, signal} from '@angular/core';
import {GlobalService} from "@app/_services/global.service";
import {AuthService} from "@app/_services/auth.service";
import {ActivatedRoute} from "@angular/router";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {NgbModal, NgbModalRef, NgbOffcanvas, NgbOffcanvasRef} from "@ng-bootstrap/ng-bootstrap";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {
    Customer,
    Feature,
    FeatureFlagHistoryEvent,
    FeatureFlagHistoryHeader,
    GetAllUsersResponse,
    UpdateFeatureFlagForUserRequest,
    UpdateFeatureFlagRequest,
    User
} from "@app/_models/api";
import {HistoryDrawerComponent} from "@app/_components/history-drawer/history-drawer.component";
import {HistoryEvent} from "@app/_components/history-drawer/history-drawer.helpers";
import {HistoryFeed} from "@app/_components/history-drawer/history-feed";
import {formatDateTime} from "@app/processing/request-history/request-history.helpers";
import {FeatureFlagsAdminService} from "@app/_services/admin/feature-flags-admin.service";
import {AsyncPipe} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

@Component({
    selector: 'app-feature-flags',
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        SortableDirective,
        ReactiveFormsModule,
        HistoryDrawerComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './feature-flags.html'
})
export class FeatureFlagsComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('editFeatureModal') editFeatureModal!: TemplateRef<any>;
    @ViewChild('addFeatureForUserModal') addFeatureForUserModal!: TemplateRef<any>;
    @ViewChild('existingFeatureUsersModal') existingFeatureUsersModal!: TemplateRef<any>;
    @ViewChild('historyDrawer') historyDrawer!: TemplateRef<any>;

    editForm: FormGroup = new FormGroup({
        code: new FormControl<string>("", Validators.required),
        description: new FormControl<string>("", Validators.required),
        isEnabled: new FormControl<boolean>(false)
    });

    userAddForm: FormGroup = new FormGroup({
        code: new FormControl<string>("", Validators.required),
        customerId: new FormControl<string | null>(null, Validators.required),
        email: new FormControl<string | null>(null, Validators.required),
        isEnabled: new FormControl<boolean>(true),
    });

    savingData: boolean = false;
    total$: Observable<number>;
    featureFlags$: Observable<Feature[]>;
    customers: Customer[] = [];
    users: User[] = [];
    loadingCustomers$ = new BehaviorSubject<boolean>(false);
    loadingUsers$ = new BehaviorSubject<boolean>(false);
    selectedFeature: Feature | null = null;

    historyCode = signal<string | null>(null);
    historyHeader = signal<FeatureFlagHistoryHeader | null>(null);
    includeUserChanges = signal(true);
    historyFeed = new HistoryFeed({
        fetchPage: page => {
            if (page === 1) {
                this.historyHeader.set(null);
            }
            return this.apiService.getFeatureFlagHistory(this.historyCode(), {
                page,
                includeUserChanges: this.includeUserChanges()
            }).pipe(
                tap(response => this.historyHeader.set(response.flag)),
                map(response => ({
                    events: response.events.map((e: FeatureFlagHistoryEvent): HistoryEvent => ({
                        correlationId: e.correlationId,
                        tableName: e.tableName,
                        record: e.code,
                        occurredAt: e.occurredAt,
                        userName: e.userName,
                        customerName: e.customerName,
                        kind: e.kind,
                        summary: e.summary,
                        rows: e.rows
                    })),
                    totalCount: response.totalCount,
                    pageSize: response.pageSize
                }))
            );
        }
    });
    protected readonly formatDateTime = formatDateTime;

    private historyDrawerRef?: NgbOffcanvasRef;
    private editFeatureModalRef!: NgbModalRef;
    private addFeatureForUserModalRef!: NgbModalRef;
    private existingFeatureUsersModalRef!: NgbModalRef;

    constructor(
        public globalService: GlobalService,
        public authService: AuthService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        public service: FeatureFlagsAdminService,
        private modalService: NgbModal,
        private offcanvasService: NgbOffcanvas) {

        this.service.data = this.route.snapshot.data["resolvedData"].data;
        this.featureFlags$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.refreshDataSource();
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    onEdit(code: string) {
        const feature = this.service.data.find(f => f.code === code);
        if (!feature) {
            this.toastService.showDanger('Feature not found', `Could not find feature with code ${code}`);
            return;
        }
        this.editForm.setValue({
            code: feature.code,
            description: feature.description,
            isEnabled: feature.isEnabled
        });
        this.editFeatureModalRef = this.modalService.open(this.editFeatureModal, {
            modalDialogClass: "modal-dialog-centered modal",
            backdrop: "static",
        });
    }

    onSaveEdit() {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const request: UpdateFeatureFlagRequest = this.editForm.getRawValue() as {
            code: string,
            description: string,
            isEnabled: boolean
        };

        this.apiService.updateFeatureFlag(request).subscribe({
            next: _ => {
                this.toastService.showSuccess('Feature Flag Updated', `Feature ${request.code} updated successfully.`);
                const idx = this.service.data.findIndex(f => f.code === request.code);
                if (idx >= 0) {
                    this.service.data[idx] = {
                        ...this.service.data[idx],
                        description: request.description,
                        isEnabled: request.isEnabled
                    };
                    this.service.refreshDataSource();
                }
                this.savingData = false;
                this.editFeatureModalRef.close();
            },
            error: err => {
                this.savingData = false;
                this.toastService.showDanger('Error Updating Feature Flag', err?.error?.detail || 'Unable to update feature flag.');
            }
        });
    }

    onUserAdd(code: string) {
        this.userAddForm.reset({code, customerId: null, userEmail: null, isEnabled: true});
        this.users = [];
        this.loadCustomersForUserAdd();
        this.addFeatureForUserModalRef = this.modalService.open(this.addFeatureForUserModal, {
            modalDialogClass: "modal-dialog-centered modal modal-lg",
            backdrop: "static",
        });
    }

    loadCustomersForUserAdd() {
        this.loadingCustomers$.next(true);
        this.apiService.getCustomers().subscribe({
            next: (response: Customer[]) => {
                this.customers = response;
                this.loadingCustomers$.next(false);
            },
            error: _ => {
                this.loadingCustomers$.next(false);
                this.toastService.showDanger("Error", "Failed to load customers");
            }
        });
    }

    onUserAddCustomerChange(event: Event) {
        const customerId = (event.target as HTMLSelectElement).value;
        this.userAddForm.controls['customerId'].setValue(customerId || null);
        this.users = [];
        if (customerId) {
            this.loadingUsers$.next(true);
            this.apiService.getUsers(customerId).subscribe({
                next: (response: GetAllUsersResponse) => {
                    this.users = response.users;
                    this.loadingUsers$.next(false);
                },
                error: _ => {
                    this.loadingUsers$.next(false);
                    this.toastService.showDanger("Error", "Failed to load users");
                }
            });
        }
    }

    onSaveUserAdd() {
        if (this.userAddForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please select a customer and user.");
            return;
        }

        const rawFormVal: UpdateFeatureFlagForUserRequest = this.userAddForm.getRawValue() as {
            code: string,
            email: string,
            isEnabled: boolean
        };

        this.savingData = true;
        this.apiService.updateFeatureForUserFlag(rawFormVal).subscribe({
            next: response => {
                this.savingData = false;
                this.toastService.showSuccess('Feature Flag Updated', `User override updated for ${rawFormVal.email}.`);
                const idx = this.service.data.findIndex(f => f.code === rawFormVal.code);
                if (idx >= 0) {
                    this.service.data[idx] = response;
                    this.service.refreshDataSource();
                }
            },
            error: err => {
                this.savingData = false;
                this.toastService.showDanger('Error Updating User Feature Flag', err?.error?.detail || 'Unable to update user feature flag.');
            }
        });
    }

    onViewUsers(feature: Feature) {
        this.selectedFeature = feature;
        this.existingFeatureUsersModalRef = this.modalService.open(this.existingFeatureUsersModal, {
            modalDialogClass: "modal-dialog-centered modal modal-lg",
            backdrop: "static",
        });
    }

    onDeleteFeatureFlagUser(email: string) {
        this.savingData = true;
        const request: UpdateFeatureFlagForUserRequest = {
            code: this.selectedFeature!.code,
            email: email,
            isEnabled: false
        };
        this.apiService.updateFeatureForUserFlag(request).subscribe({
            next: _ => {
                this.savingData = false;
                this.toastService.showSuccess('Feature Flag Updated', `User override removed for ${request.code}.`);
                const idx = this.service.data.findIndex(f => f.code === request.code);
                if (idx >= 0) {
                    let existingUsers = this.service.data[idx].users || [];
                    const currentUserIdx = existingUsers
                        .findIndex(u => u.email === request.email);

                    if (currentUserIdx >= 0) {
                        existingUsers.splice(currentUserIdx, 1);
                    }
                    this.service.data[idx] = {...this.service.data[idx], users: existingUsers} as Feature;
                    this.service.refreshDataSource();
                }
            },
            error: err => {
                this.savingData = false;
                this.toastService.showDanger('Error Updating User Feature Flag', err?.error?.detail || 'Unable to update user feature flag.');
            }
        });
    }

    onHistory(code: string | null) {
        this.historyCode.set(code);
        this.includeUserChanges.set(true);
        this.historyFeed.load(1);
        const drawerRef = this.offcanvasService.open(this.historyDrawer, {
            position: "end",
            panelClass: "history-offcanvas",
            ariaLabelledBy: "history-drawer-title"
        });
        this.historyDrawerRef = drawerRef;
        // hidden fires after the slide-out ends, by which time another drawer may own the feed's request.
        drawerRef.hidden.subscribe(() => {
            if (this.historyDrawerRef === drawerRef) {
                this.historyFeed.cancel();
            }
        });
    }

    onIncludeUserChanges(include: boolean) {
        this.includeUserChanges.set(include);
        this.historyFeed.load(1);
    }

    closeHistoryDrawer() {
        this.historyDrawerRef?.dismiss();
    }

    closeExistingFeatureUsersModal() {
        this.existingFeatureUsersModalRef.close();
    }

    closeAddFeatureUsersModal() {
        this.addFeatureForUserModalRef.close();
    }

    closeEditFeatureModal() {
        this.editFeatureModalRef.close();
    }
}
